import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import moment from 'moment';
import { getNotification_action, readNotification_action, getNotificationPreview_action } from '../actions/notification_action/notification_action';

const STATIC_NOTIFICATIONS = [
];

// First alphanumeric character of the subject, used as the avatar letter.
function getInitial(text) {
    const plain = String(text ?? "").replace(/<[^>]*>/g, "").trim();
    const match = plain.match(/[A-Za-z0-9]/);
    return (match ? match[0] : plain.charAt(0) || "?").toUpperCase();
}

function formatSentDate(value) {
    if (!value) return "";
    // Show the value exactly as sent (treat the Z time as-is, no local shift).
    const parsed = moment.utc(value);
    return parsed.isValid() ? parsed.format("DD-MM-YYYY HH:mm") : value;
}

function mapApiNotification(item, index) {
    const subject = item.Subject ?? item.NotificationMessage ?? "New notification";
    return {
        id: item.IntimationNotificationID ?? index + 1,
        intimationNotificationID: item.IntimationNotificationID ?? null,
        // Needed to fetch the full HTML body (with tables) from the preview endpoint.
        feaIntimationRecipientID: item.FeaIntimationRecipientID ?? item.FeaIntimationRecipientId ?? null,
        title: subject,
        initial: getInitial(subject),
        body: item.Body ?? item.MessageBody ?? item.EmailBody ?? item.BodyHtml ?? item.Content ?? null,
        time: formatSentDate(item.SentDate ?? item.CreatedAt ?? item.NotificationTime ?? item.Time),
        unread: item.RecipientStatus !== 'Read',
        type: "info",
    };
}

// The preview endpoint may return the HTML body under any of these shapes.
function extractPreviewBody(res) {
    const root = res?.data ?? res;
    const pick = (obj) =>
        obj?.FullHtml ?? obj?.HtmlContent ?? obj?.Body ?? obj?.MessageBody ?? obj?.EmailBody ??
        obj?.BodyHtml ?? obj?.Content ?? null;
    if (Array.isArray(root)) return pick(root[0]) ?? null;
    return pick(root) ?? null;
}

const useNotifications = () => {
    const dispatch = useDispatch();
    const [notifications, setNotifications] = useState([]);
    const [previewItem, setPreviewItem] = useState(null);
    const [showDrawer, setShowDrawer] = useState(false);

    useEffect(() => {
        dispatch(getNotification_action())
            .then((res) => {
                const list = res?.data ?? res;
                if (list && Array.isArray(list) && list.length > 0) {
                    setNotifications(list.map(mapApiNotification));
                } else {
                    setNotifications(STATIC_NOTIFICATIONS);
                }
            })
            .catch(() => {
                setNotifications(STATIC_NOTIFICATIONS);
            });
    }, [dispatch]);

    const unreadCount = notifications.filter((n) => n.unread).length;

    const markAllRead = () => {
        notifications
            .filter((n) => n.unread)
            .forEach((n) => {
                const notifId = n.intimationNotificationID ?? n.id;
                dispatch(readNotification_action(notifId)).catch(() => { });
            });
        setNotifications((prev) => prev.map((n) => ({ ...n, unread: false })));
    };

    const markOneRead = (id) => {
        setNotifications((prev) =>
            prev.map((n) => (n.id === id ? { ...n, unread: false } : n))
        );
        const notif = notifications.find((n) => n.id === id);
        const notifId = notif?.intimationNotificationID ?? id;
        dispatch(readNotification_action(notifId)).catch(() => { });
    };

    const openPreview = (id) => {
        const notif = notifications.find((n) => n.id === id);
        if (!notif) return;

        // Open immediately. If the list item has no HTML body, fetch the full
        // body (with tables) from the preview endpoint and fill it in.
        const needsFetch = !notif.body && notif.feaIntimationRecipientID != null;
        setPreviewItem({ ...notif, bodyLoading: needsFetch });

        // Mark as read when preview opens
        setNotifications((prev) =>
            prev.map((n) => (n.id === id ? { ...n, unread: false } : n))
        );
        const notifId = notif.intimationNotificationID ?? id;
        dispatch(readNotification_action(notifId)).catch(() => { });

        if (needsFetch) {
            dispatch(getNotificationPreview_action(notif.feaIntimationRecipientID))
                .then((res) => {
                    const body = extractPreviewBody(res);
                    // Only update if this preview is still the one being shown.
                    setPreviewItem((prev) =>
                        prev && prev.id === id ? { ...prev, body, bodyLoading: false } : prev
                    );
                })
                .catch(() => {
                    setPreviewItem((prev) =>
                        prev && prev.id === id ? { ...prev, bodyLoading: false } : prev
                    );
                });
        }
    };

    const closePreview = () => setPreviewItem(null);

    const openDrawer = () => setShowDrawer(true);
    const closeDrawer = () => setShowDrawer(false);

    return { notifications, unreadCount, markAllRead, markOneRead, previewItem, openPreview, closePreview, showDrawer, openDrawer, closeDrawer };
};

export default useNotifications;
